import apisauce from 'apisauce'
import { getSessionToken } from '@shopify/app-bridge-utils'
import createApp from '@shopify/app-bridge'

export default {
  create: () => {
    const api = apisauce.create({
      baseURL: process.env.BASE_URL,
      headers: {
        // Origin: 'https://mukul.ngrok.io',
        'Content-Type': 'application/json',
        // Authorization: `Bearer ${accessToken}`
        'Access-Control-Allow-Origin': '*',
      },
      timeout: 30000,
    })

    api.axiosInstance.interceptors.request.use(function (config) {
      return getSessionToken(window.appointo_app_bridge) // requires an App Bridge instance
        .then((token) => {
          // append your request headers with an authenticated token
          config.headers['Authorization'] = `Bearer ${token}`
          return config
        })
    })

    return getApis(api)
  },
}

export const getApis = (api) => {
  return {
    updateSettings: (id, params) => api.put(`settings/${id}`, params),
    updateContactButton: (id, params) =>
      api.put(`contact_buttons/${id}`, params),
    getProductDetails: (params) => api.get(`products`, params),
    getAppointments: (params) => api.get(`appointments`, params),
    upsertAppointments: (params) => api.put(`appointments/upsert`, params),
    getCalendlyEvents: (_) => api.get(`settings/calendly_events`),
    getProducts: (params) => api.get(`products/product_list`, params),
    getShopifyProducts: (params) =>
      api.get(`products/shopify_products`, params),
    updateProduct: (params) => api.put(`products/upsert`, params),
    upgradePlan: (params) => api.put(`plans/upgrade`, params),
    cancelPlan: (_) => api.put(`plans/cancel`),
    sendTestEmail: (params) => api.put(`appointments/send_email`, params),
    singleUpsertAppointments: (params) =>
      api.put(`appointments/single_upsert`, params),
    multipleUpsertAppointments: (params) =>
      api.put(`appointments/multiple_upsert`, params),
    addCalendlyToken: (params) => api.post(`settings/add_token`, params),
    removeCalendlyToken: (params) =>
      api.delete(`settings/remove_token`, params),
    updateAppointmentConfig: (params) =>
      api.put(`appointments/${params.id}/update_configuration`, params),
    getBookings: (params) => api.get(`appointments/bookings`, params),
    getBookingDetails: (params) => api.get(`appointments/bookings`, params),
    getBookingSubscription: (id) => api.get(`appointment_subscriptions/${id}`),
    getLegacyBookingSubscription: (params) =>
      api.get(`appointments/subscription_detail`, params),
    cancelBooking: (params) =>
      api.delete(`appointments/cancel_booking`, params),
    restoreBooking: (id, params) => api.put(`bookings/${id}/restore`, params),
    editBooking: (id, params) => api.put(`bookings/${id}`, params),
    rescheduleBooking: (params) =>
      api.put(`appointments/reschedule_booking`, params),
    updateShop: (params) => api.put(`settings/shop_setting`, params),
    deleteCustomField: (apptId, params) =>
      api.delete(`appointments/${apptId}/delete_custom_field`, params),
    getConflicts: (params) => api.get(`/appointments/conflicts`, params),
    getSubscriptions: (params) => api.get(`/appointment_subscriptions`, params),
    getLegacySubscriptions: () => api.get(`/appointments/subscriptions`),
    deleteSubscription: (id) => api.delete(`appointment_subscriptions/${id}`),
    getTextEmailSettings: () => api.get(`/email_settings`),
    getEmailSettings: () => api.get(`settings/get_sender_signature`),
    sendVerificationEmail: () =>
      api.put(`settings/resend_signature_verification`),
    updateEmailSettings: (params) =>
      api.put(`settings/upsert_sender_signature`, params),
    upsertTextEmailSettings: (params) =>
      api.put(`email_settings/upsert`, params),
    getShopifyCustomers: (params) => api.get(`customers`, params),
    generateBookingLink: (params) =>
      api.post(`appointments/create_booking`, params),
    connectGoogleCalendar: (params) =>
      api.post(`settings/google_calendar_auth`, params),
    removeGoogleCalendar: (params) => api.delete(`settings/disconnect_google`),
    getTeams: () => api.get(`teams`),
    createTeam: (params) => api.post(`teams`, params),
    deleteTeam: (id) => api.delete(`teams/${id}`),
    sendMemberVerificationEmail: (id) =>
      api.put(`teams/${id}/send_confirmation`),
    removeZoom: (_) => api.delete(`settings/disconnect_zoom`),
    updateTeamMember: (id, params) => api.put(`teams/${id}`, params),
    getValidTeamMemberForBooking: (params) =>
      api.get(`/appointments/valid_tms_for_reassign`, params),
    reassignTeamMember: (params) =>
      api.put(`appointments/reassign_team_member_to_booking`, params),
    getWorkflows: () => api.get(`workflows`),
    createWorkflow: (params) => api.put(`workflows/upsert`, params),
    deleteWorkflow: (id) => api.delete(`workflows/${id}`),
    updateWorkflow: (id, params) => api.put(`workflows/upsert`, params),
    getWorkflowTypes: () => api.get(`workflows/types`),
    searchAppointments: (params) => api.get(`/appointments/search`, params),
    getWorkflowDetails: (id) => api.get(`workflows/${id}`),
    getCalendarBookings: (params) =>
      api.get(`appointments/calendar_bookings`, params),
    getStats: (params) => api.get(`/v2/stats`, params),
    updateShop: (params) => api.put(`settings/shop_setting`, params),
    updateTextSetting: (params) =>
      api.put(`text_settings/${params.id}`, params),
    getUsageCharge: () => api.get(`text_settings/usage_charge`),
    createNote: (id, params) => api.put(`bookings/${id}/upsert_note`, params),
    deleteNote: (id, params) =>
      api.delete(`bookings/${id}/delete_note`, params),
    onSync: () => api.put(`settings/sync_data`),
    updateCustomer: (id, params) => api.put(`customers/${id}`, params),
    getTags: () => api.get(`groups`),
    createTag: (params) => api.post(`groups`, params),
    deleteTag: (id) => api.delete(`groups/${id}`),
    sendManualEmail: (params) =>
      api.put(`appointments/send_manual_email`, params),
    getActivities: (params) =>
      api.get(`/appointments/booking_activities?${params}`, params),
    getCancelledActivities: (params) =>
      api.get(
        `/appointments/cancelled_booking_activities?cancelled_booking_id=${params.booking_id}`
      ),
    createCustomer: (params) => api.post(`customers`, params),
    resendEmail: (id, params) => api.get(`bookings/${id}/resend_email`, params),
    getAllCustomerTags: () => api.get(`customer_tagger/all_tags`),
    updateAppointment: (params) =>
      api.put(`appointments/change_appointment`, params),
    getAllCalendars: () => api.get('settings/all_calendars'),
    getAppointmentSubscriptions: () => api.get('selling_plan_groups'),
    getAppointmentSubscriptionsDetail: (id) =>
      api.get(`selling_plan_groups/${id}`),
    upsertAppointmentSubscription: (params) =>
      api.put('selling_plan_groups/upsert', params),
    searchShopifyProducts: (params) =>
      api.get(`selling_plan_groups/search_products`, params),
    deleteAppointmentSubscription: (id) =>
      api.delete(`selling_plan_groups/${id}`),
    updateCustomerPaymentMethod: (params) =>
      api.put(
        `appointment_subscriptions/update_customer_payment_method`,
        params
      ),
    createManualSubscription: (params) =>
      api.post(`/appointment_subscriptions`, params),
    createManualRecurringSubscription: (params) =>
      api.post(`/appointment_subscriptions/recurring_subscription`, params),
    bookingsByCustomer: (params) =>
      api.get(`appointment_subscriptions/unconnected_customers`, params),
    connectBookingToSubscription: (params) =>
      api.put(`appointment_subscriptions/connect_booking`, params),
    checkAppBlock: () => api.get(`settings/check_app_blocks`),
    startImport: (params) => api.put(`import_csv/upload`, params),
    getCustomers: (params) => api.get('customers/list', params),
    getCustomerDetails: (params) => api.get(`customers/customer_info`, params),
    getCustomerMessages: (params) => api.get(`customers/messages`, params),
    sendMessage: (params) => api.put(`customers/send_sms`, params),
    markAsRead: (params) => api.put(`customers/mark_sms_as_read`, params),
    updateSubscriptionDetails: (params) =>
      api.put(`appointment_subscriptions/${params.id}`, params),
    retrySubscriptionBilling: (params) =>
      api.put(`appointment_subscriptions/retry_billing`, params),
    onGenerateApiToken: () => api.put(`settings/generate_api_token`),
    onGenerateZapierApiToken: () =>
      api.put(`settings/generate_api_zapier_token`),
    deleteConflict: (id) => api.delete(`appointments/delete_conflict`, { id }),
    getBookingWorkflows: (params) => api.get(`workflows/activities`, params),
    getCancelledBookings: (params) =>
      api.get(`appointments/cancelled_bookings`, params),
    getCancelledBookingDetails: (params) =>
      api.get(`appointments/cancelled_bookings`, params),
    upsertBookingStatus: (params) =>
      api.put(`settings/upsert_booking_statuses`, params),
    deleteBookingStatus: (id) =>
      api.delete(`settings/delete_booking_status?id=${id}`),
    getWidgets: () => api.get(`appointo_widgets`),
    createWidget: (params) => api.post(`appointo_widgets`, params),
    deleteWidget: (id) => api.delete(`appointo_widgets/${id}`),
    updateWidget: (id, params) => api.put(`appointo_widgets/${id}`, params),
    getWidgetDetails: (id) => api.get(`appointo_widgets/${id}`),
    updateAllCustomer: (params) =>
      api.put(`customers/update_cust_by_email_and_name`, params),
    getWaitlists: (params) => api.get(`waitlist`, params),
    upsertWaitlist: (params) => api.put(`waitlist/${params.id}`, params),
    addToWaitlist: (params) => api.post(`waitlist`, params),
    deleteAppointmentAddOn: (id) =>
      api.delete(`appointments/delete_addon_product?id=${id}`),
    sendCustomerioEvents: (params) =>
      api.put(`settings/send_customerio_events`, params),
    createShopifyProduct: (params) =>
      api.post(`products/create_shopify_product`, params),
    checkCouponCode: (params) => api.put(`plans/verify_coupon_code`, params),
    resendConfirmationEmail: (params) =>
      api.put(`bookings/${params.booking_id}/resend_email`, {
        customer_id: params.customer_id,
      }),
    getDefaultLocale: (locale) =>
      api.get(`/settings/default_locale?locale=${locale}`),
    getOrderTags: () => api.get(`shopify_tags`),
    updateOrderTags: (params) => api.put(`shopify_tags/update`, params),
    connectAppleCalendar: (params) =>
      api.post(`settings/apple_calendar_auth`, params),
    disconnectAppleCalendarRequest: (params) =>
      api.delete(`settings/disconnect_apple_calendar`, params),
    disconnectHubspot: () => api.delete(`settings/disconnect_hubspot`),
    verifyTextSettings: (params) => api.put(`text_settings/verify`),
    calculateRefund: (params) =>
      api.post(`bookings/${params.id}/calculate_refund`, params),
    getTimezone: () => api.get('scripttag/timezones'),
    deleteSignature: () => api.delete(`settings/delete_sender_signature`),
    getUsageData: (params) => api.get('settings/appointo_usage', params),
    loginAsTeamMember: (id) => api.get(`teams/${id}/dashboard_login_url`),
    connectKlaviyo: (params) => api.get(`klaviyo/dashboard/auth`, params),
    disconnectKlaviyo: () => api.delete(`klaviyo/dashboard/disconnect`),
    updateContractPrice: (params) =>
      api.put(
        `appointment_subscriptions/update_subscription_contract_price`,
        params
      ),
    sendCustomerPortalLink: (params) =>
      api.put(
        `/appointment_subscriptions/send_customer_portal_link_email`,
        params
      ),
    skipCycle: (params) =>
      api.put(`/appointment_subscriptions/skip_schedule`, params),
    updateTrial: (params) =>
      api.put(`settings/update_trial_expires_at`, params),
    chargeSubscription: (params) =>
      api.put(`appointment_subscriptions/charge_now`, params),
    scheduleSubscriptionDate: (params) =>
      api.put(`appointment_subscriptions/change_schedule_date`, params),
    updateContractInterval: (params) =>
      api.post(`appointment_subscriptions/update_contract_interval`, params),
    me: () => api.get('/settings/me'),
    deleteCustomQuestions: (params) =>
      api.delete(`appointments/delete_all_custom_fields`, params),
  }
}
